import { useEffect, useState } from "react";
import api, { fmtTRY } from "../lib/api";
import { Area, AreaChart, Tooltip, XAxis, YAxis, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { ArrowDownRight, ArrowUpRight, Wallet, TrendingUp, TrendingDown, CalendarClock, Sparkles, AlertTriangle, PieChart as PieIcon, Info } from "lucide-react";
import { Link } from "react-router-dom";

const COLORS = ["#06B6D4", "#10B981", "#8B5CF6", "#F59E0B", "#F43F5E", "#38BDF8", "#A78BFA", "#FBBF24"];

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setS(r.data));
    api.get("/ai/insights").then((r) => setInsights(r.data.insights || []));
  }, []);

  if (!s) return <div className="text-slate-500">Yükleniyor...</div>;

  const kpis = [
    { label: "Toplam Bakiye", value: s.total_balance_try, icon: Wallet, color: "cyan" },
    { label: "Aylık Gelir", value: s.monthly_income_try, icon: TrendingUp, color: "emerald" },
    { label: "Aylık Gider", value: s.monthly_expense_try, icon: TrendingDown, color: "coral" },
    { label: "Net Nakit Akışı", value: s.net_flow_try, icon: s.net_flow_try >= 0 ? ArrowUpRight : ArrowDownRight, color: s.net_flow_try >= 0 ? "emerald" : "coral" },
  ];

  return (
    <div className="space-y-6 nf-fade-up" data-testid="dashboard-root">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-mono-num text-slate-500">
            <span className="nf-live-dot" /> Canlı Nakit Akışı
          </div>
          <h1 className="font-head text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-2">
            Finans Komuta Merkezi
          </h1>
        </div>
        {/* FX ticker */}
        <div className="overflow-hidden w-full md:w-auto md:max-w-xs nf-card px-4 py-2.5">
          <div className="text-[10px] uppercase tracking-widest font-mono-num text-slate-500 mb-1">Döviz Kurları</div>
          <div className="flex gap-4 text-sm font-mono-num">
            {Object.entries(s.fx_rates).filter(([k]) => k !== "TRY").map(([k, v]) => (
              <div key={k} className="flex items-center gap-1.5">
                <span className="text-slate-400">{k}/TRY</span>
                <span className="font-semibold text-cyan-400">{v.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map(({ label, value, icon: Icon, color }) => (
          <div key={label} data-testid={`kpi-${label}`} className={`nf-card p-5 relative overflow-hidden ${color === "cyan" ? "nf-glow-cyan" : ""}`}>
            <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full bg-${color}-500/5`} />
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-widest font-mono-num text-slate-500">{label}</span>
              <Icon className={`w-4 h-4 text-${color}-400`} />
            </div>
            <div className="font-head font-extrabold text-2xl lg:text-3xl font-mono-num">{fmtTRY(value)}</div>
          </div>
        ))}
      </div>

      {/* Main grid */}
      <div className="grid lg:grid-cols-12 gap-6">
        {/* Cash flow chart */}
        <div className="lg:col-span-8 nf-card p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-xs uppercase tracking-widest font-mono-num text-slate-500">Son 30 Gün</div>
              <h2 className="font-head text-xl font-bold mt-1">Nakit Akışı Grafiği</h2>
            </div>
            <div className="flex gap-3 text-xs">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />Gelir</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-coral-400" style={{ backgroundColor: "#F43F5E" }} />Gider</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={s.cashflow_30d}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#F43F5E" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#F43F5E" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#64748B" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#64748B" fontSize={10} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ background: "#121722", border: "1px solid #334155", borderRadius: 10, fontSize: 12 }}
                labelStyle={{ color: "#94A3B8" }}
                formatter={(v) => fmtTRY(v)}
              />
              <Area type="monotone" dataKey="gelir" stroke="#10B981" fill="url(#g1)" strokeWidth={2} />
              <Area type="monotone" dataKey="gider" stroke="#F43F5E" fill="url(#g2)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* AI insights */}
        <div className="lg:col-span-4 nf-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-purple-400" />
            <h2 className="font-head text-xl font-bold">AI İçgörüler</h2>
          </div>
          <div className="space-y-3">
            {insights.length === 0 && <div className="text-sm text-slate-500">Henüz içgörü yok.</div>}
            {insights.map((ins, i) => {
              const color = ins.type === "warning" ? "coral" : ins.type === "success" ? "emerald" : "cyan";
              const Icon = ins.type === "warning" ? AlertTriangle : ins.type === "success" ? TrendingUp : Info;
              return (
                <div key={i} className={`p-3 rounded-lg border bg-${color}-500/5 border-${color}-500/20`} style={{
                  background: color === "coral" ? "rgba(244,63,94,0.05)" : color === "emerald" ? "rgba(16,185,129,0.05)" : "rgba(6,182,212,0.05)",
                  borderColor: color === "coral" ? "rgba(244,63,94,0.2)" : color === "emerald" ? "rgba(16,185,129,0.2)" : "rgba(6,182,212,0.2)",
                }}>
                  <div className="flex items-start gap-2">
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0`} style={{
                      color: color === "coral" ? "#F43F5E" : color === "emerald" ? "#10B981" : "#06B6D4"
                    }} />
                    <div>
                      <div className="text-sm font-semibold">{ins.title}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{ins.detail}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <Link to="/ai" data-testid="goto-ai-link" className="mt-4 inline-flex items-center gap-1 text-sm text-cyan-400 hover:text-cyan-300">
            AI danışmana sor <ArrowUpRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {/* Categories */}
        <div className="lg:col-span-5 nf-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <PieIcon className="w-4 h-4 text-cyan-400" />
            <h2 className="font-head text-xl font-bold">Gider Dağılımı</h2>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={s.top_categories} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                {s.top_categories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="#0A0D14" strokeWidth={2} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#121722", border: "1px solid #334155", borderRadius: 10, fontSize: 12 }} formatter={(v) => fmtTRY(v)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {s.top_categories.slice(0, 6).map((c, i) => (
              <div key={c.name} className="flex items-center gap-2 text-xs">
                <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="text-slate-400 truncate flex-1">{c.name}</span>
                <span className="font-mono-num text-slate-300">{fmtTRY(c.value)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming payments */}
        <div className="lg:col-span-7 nf-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-amber-400" />
              <h2 className="font-head text-xl font-bold">Yaklaşan İşlemler</h2>
            </div>
            <Link to="/odemeler" className="text-xs text-cyan-400 hover:text-cyan-300" data-testid="goto-payments">Tümü →</Link>
          </div>
          <div className="space-y-2">
            {s.upcoming_payments.length === 0 && <div className="text-sm text-slate-500">Yaklaşan işlem yok.</div>}
            {s.upcoming_payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/40 border border-slate-800 hover:border-slate-700 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${p.type === "tahsilat" ? "bg-emerald-500/10" : "bg-coral-500/10"}`} style={{ backgroundColor: p.type === "tahsilat" ? "rgba(16,185,129,0.1)" : "rgba(244,63,94,0.1)" }}>
                    {p.type === "tahsilat" ? <ArrowDownRight className="w-4 h-4 text-emerald-400" /> : <ArrowUpRight className="w-4 h-4" style={{ color: "#F43F5E" }} />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{p.description}</div>
                    <div className="text-[11px] text-slate-500 font-mono-num uppercase tracking-widest">
                      {new Date(p.due_date).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" })} • {p.type}
                    </div>
                  </div>
                </div>
                <div className={`font-mono-num font-semibold ${p.type === "tahsilat" ? "text-emerald-400" : ""}`} style={{ color: p.type === "tahsilat" ? "#10B981" : "#F43F5E" }}>
                  {p.type === "tahsilat" ? "+" : "−"}{p.amount.toLocaleString("tr-TR")} {p.currency}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
